import React, { useState } from 'react';
import { Components } from './components';

const { Header, Footer } = Components;

const ForAttorneys = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    lawFirm: '',
    stateOfPractice: '',
    areasOfSpecialization: [],
    additionalInfo: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [showAIExpert, setShowAIExpert] = useState(false);
  const [showUSCISScanner, setShowUSCISScanner] = useState(false);

  const platformFeatures = [
    {
      icon: '🔍',
      title: 'AI Case Analysis',
      description: 'Get instant case strength assessments, identify potential issues early, and receive strategic recommendations powered by analysis of thousands of successful cases.'
    },
    {
      icon: '📋',
      title: 'Automated Document Review',
      description: 'Upload client documents and receive comprehensive analysis, completeness checks, and recommendations for strengthening applications.'
    },
    {
      icon: '😊',
      title: 'Client Self-Service Portal',
      description: 'Empower clients with guided form completion, document preparation, and 24/7 support, reducing your administrative burden.'
    },
    {
      icon: '📄',
      title: 'RFE/NOID Response Assistant',
      description: 'Quickly analyze USCIS notices, identify required evidence, and generate comprehensive response strategies with supporting documentation checklists.'
    },
    {
      icon: '📊',
      title: 'Practice Analytics',
      description: 'Track case outcomes, identify success patterns, and optimize your practice with data-driven insights and performance metrics.'
    },
    {
      icon: '🔒',
      title: 'Compliance & Security',
      description: 'Built with attorney-client privilege in mind, featuring bank-level security, audit trails, and full compliance with legal industry standards.'
    }
  ];

  const specializedAgents = [
    '👨‍👩‍👧‍👦 Family-Based Immigration Specialist',
    '💼 Work Visa & Employment Expert',
    '🛡️ Asylum & Protection Advocate',
    '📚 Student & Exchange Visa Guide',
    '🇺🇸 Naturalization & Citizenship Coach',
    '📋 RFE/NOID Response Specialist',
    '🔄 DACA/TPS Renewal Expert',
    '🧳 Visitor & Tourist Visa Assistant'
  ];

  const areasOfSpecialization = [
    'Family-Based',
    'Work Visa',
    'Asylum',
    'Citizenship',
    'RFE/NOID',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      areasOfSpecialization: checked 
        ? [...prev.areasOfSpecialization, name]
        : prev.areasOfSpecialization.filter(area => area !== name)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔵 Attorney waitlist form submission started');
    console.log('🔵 Form data:', formData);
    
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Validate required fields
      const requiredFields = {
        fullName: 'Full Name',
        email: 'Email Address',
        lawFirm: 'Law Firm/Practice Name',
        stateOfPractice: 'State of Practice'
      };

      for (const [field, label] of Object.entries(requiredFields)) {
        if (!formData[field].trim()) {
          console.log('❌ Validation failed for:', field);
          setSubmitStatus({
            type: 'error',
            message: `${label} is required`
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        console.log('❌ Email validation failed');
        setSubmitStatus({
          type: 'error',
          message: 'Please enter a valid email address'
        });
        setIsSubmitting(false);
        return;
      }

      console.log('✅ Validation passed, submitting to backend');

      // Submit to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      console.log('🔵 Backend URL:', backendUrl);
      
      const response = await fetch(`${backendUrl}/api/attorney/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('🔵 Response status:', response.status);
      
      const data = await response.json();
      console.log('🔵 Response data:', data);

      if (response.ok) {
        console.log('✅ Submission successful!');
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your interest! We\'ll be in touch soon with early access details.'
        });
        
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          lawFirm: '',
          stateOfPractice: '',
          areasOfSpecialization: [],
          additionalInfo: ''
        });
      } else {
        console.log('❌ Submission failed:', data.detail);
        setSubmitStatus({
          type: 'error',
          message: data.detail || 'An error occurred. Please try again.'
        });
      }
    } catch (error) {
      console.error('🔴 Attorney waitlist error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
      console.log('🔵 Form submission completed');
    }
  };

  // Header handler functions
  const handleOpenAIExpert = () => {
    setShowAIExpert(true);
  };

  const handleOpenUSCISScanner = () => {
    setShowUSCISScanner(true);
  };

  const handleAuthAction = (mode = 'signin') => {
    // For now, just log the action - can be enhanced later
    console.log('Auth action:', mode);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <Header 
        onOpenAIExpert={handleOpenAIExpert}
        onOpenUSCISScanner={handleOpenUSCISScanner}
        user={null} // For now, user is null - can be enhanced with auth context later
        onAuthAction={handleAuthAction}
      />
      
      {/* Main Content */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800">
      {/* Hero Section */}
      <div className="px-6 py-16">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Immigrantli for Attorneys
          </h1>
          <p className="text-xl text-emerald-400 mb-4">
            AI-powered support that complements your practice — not replaces it.
          </p>
          <p className="text-gray-400 text-lg max-w-4xl mx-auto">
            Immigrantli's AI-powered platform helps immigration attorneys streamline case management, improve client outcomes, and scale their practice efficiently.
          </p>
        </div>
      </div>

      {/* What Immigrantli Can Do Section */}
      <div className="px-6 py-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-4 text-center">
            🔍 What Immigrantli Can Do:
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {platformFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-800 p-8 rounded-xl border border-gray-700 hover:border-emerald-500 transition-all duration-300">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Specialized AI Agents Section */}
      <div className="px-6 py-16 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-8 text-center">
            Specialized AI Agents for Every Case Type
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specializedAgents.map((agent, index) => (
              <div key={index} className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-emerald-500 transition-all duration-300">
                <p className="text-white font-medium text-center">{agent}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-xl text-emerald-400 font-semibold mb-2">
              You remain the legal expert.
            </p>
            <p className="text-gray-400 text-lg">
              We just help users get organized, confident, and prepared.
            </p>
          </div>
        </div>
      </div>

      {/* Waitlist Section */}
      <div className="px-6 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Left Column - Information */}
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Transform Your Practice?
              </h2>
              <p className="text-gray-400 mb-8 text-lg">
                Join the waitlist for early access to Immigrantli's attorney platform and be among the first to experience the future of immigration law practice.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full mt-2 mr-4"></div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Early Access Benefits</h4>
                    <p className="text-gray-400">Be first to access new features and provide input on platform development</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full mt-2 mr-4"></div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Exclusive Pricing</h4>
                    <p className="text-gray-400">Lock in special rates for early adopters</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full mt-2 mr-4"></div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Priority Support</h4>
                    <p className="text-gray-400">Direct line to our development team for feature requests</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Waitlist Form */}
            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700">
              <h3 className="text-2xl font-semibold text-emerald-400 mb-6 text-center">
                Join the Waitlist
              </h3>
              
              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* Status Message */}
                {submitStatus.message && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitStatus.type === 'success' 
                      ? 'bg-emerald-900/30 border border-emerald-400 text-emerald-400' 
                      : 'bg-red-900/30 border border-red-400 text-red-400'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="lawFirm"
                    placeholder="Law Firm/Practice Name *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.lawFirm}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                
                <input
                  type="text"
                  name="stateOfPractice"
                  placeholder="State of Practice *"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                  value={formData.stateOfPractice}
                  onChange={handleInputChange}
                  required
                />
                
                <div>
                  <label className="block text-white mb-3 font-medium">Areas of Specialization</label>
                  <div className="grid grid-cols-2 gap-3">
                    {areasOfSpecialization.map(area => (
                      <label key={area} className="flex items-center text-gray-300 cursor-pointer py-2">
                        <input
                          type="checkbox"
                          name={area}
                          className="mr-3 accent-emerald-400 w-5 h-5 md:w-4 md:h-4"
                          checked={formData.areasOfSpecialization.includes(area)}
                          onChange={handleCheckboxChange}
                        />
                        <span className="text-base md:text-sm">{area}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <textarea
                  name="additionalInfo"
                  placeholder="Message (Optional)"
                  rows="4"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                />
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors disabled:bg-emerald-700 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Joining Waitlist...
                    </div>
                  ) : (
                    'Join Waitlist'
                  )}
                </button>
                
                <p className="text-gray-400 text-sm text-center">
                  Your information is secure and will only be used to contact you about early access opportunities.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ForAttorneys;